
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web

import searchResultsReducer from './searchResultsSlice';
import pricingHistoryReducer from './pricingHistorySlice';
import eventsReducer from './eventsSlice';
import quarterlyEventsReducer from './quarterlyEventsSlice';
import supaCompaniesReducer from './supaCompaniesSlice';
import supaPagesReducer from './supaPagesSlice';
import companiesFilterReducer from './companiesFilterSlice';
import supaEventsReducer from './supaEventsSlice';
import companyReducer from './companySlice';
import tagsReducer from './tagsSlice';
import versionsReducer from './versionsSlice';
import mapsReducer from './mapsSlice';
import graphqlReducer from './graphqlSlice';
import diffsReducer from './diffsSlice';
import analysisReducer from './analysisSlice';
import externalDiffsReducer from './externalDiffsSlice';

// Define the persist configuration
const persistConfig = {
  key: 'root',
  storage,
  // Whitelist specific reducers to persist (only persist what's needed)
  whitelist: [
    'supaCompanies',
    'supaPages',
    'companiesFilter',
    'pricingHistory',
    'company', // Add company to whitelist to persist between page reloads
    'versions', // Add versions to whitelist to persist between page reloads
    'maps', // Add maps to whitelist to persist between page reloads
    'graphql', // Add graphql to whitelist to persist between page reloads
    'analysis' // Add analysis to persist analyze tab state
  ],
};

// Combine all reducers
const rootReducer = combineReducers({
  searchResults: searchResultsReducer,
  pricingHistory: pricingHistoryReducer,
  events: eventsReducer,
  quarterlyEvents: quarterlyEventsReducer,
  supaCompanies: supaCompaniesReducer,
  supaPages: supaPagesReducer,
  companiesFilter: companiesFilterReducer,
  supaEvents: supaEventsReducer,
  company: companyReducer,
  tags: tagsReducer,
  versions: versionsReducer,
  maps: mapsReducer,
  graphql: graphqlReducer,
  diffs: diffsReducer,
  analysis: analysisReducer,
  externalDiffs: externalDiffsReducer,
});

// Create a persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create the store with the persisted reducer
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types to avoid non-serializable warnings
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

// Create the persistor
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
